'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

interface Stat {
  id: number;
  value: number;
  suffix: string;
  label: string;
  description: string;
  dot: 'lt-dot-blue' | 'lt-dot-gold';
}

interface StatsSectionProps {
  className?: string;
}

const Counter = ({ end, suffix, start }: { end: number; suffix: string; start: boolean }) => {
  const [count, setCount] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    if (!start || done.current) return;
    done.current = true;
    const duration = 1600;
    const t0 = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - t0) / duration, 1);
      setCount(Math.floor(end * (1 - Math.pow(1 - p, 4))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [start, end]);

  return (
    <span data-component-id="src/app/homepage/components/StatsSection.tsx:37:4" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="37" data-component-end-line="40" data-component-file="StatsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const StatsSection = ({ className = '' }: StatsSectionProps) => {
  const [sectionRef, isVisible] = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  const stats: Stat[] = [
    {
      id: 1,
      value: 15000,
      suffix: '+',
      label: 'Active Students',
      description: 'Students using the platform daily',
      dot: 'lt-dot-gold',
    },
    {
      id: 2,
      value: 98,
      suffix: '%',
      label: 'Satisfaction Rate',
      description: 'Positive feedback from users',
      dot: 'lt-dot-blue',
    },
    {
      id: 3,
      value: 50000,
      suffix: '+',
      label: 'Appointments Completed',
      description: 'Successful counseling sessions',
      dot: 'lt-dot-blue',
    },
    {
      id: 4,
      value: 24,
      suffix: '/7',
      label: 'Support Available',
      description: 'Round-the-clock assistance',
      dot: 'lt-dot-gold',
    },
  ];

  return (
    <section data-component-id="src/app/homepage/components/StatsSection.tsx:83:4" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="83" data-component-end-line="98" data-component-file="StatsSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" ref={sectionRef} className={`bg-[var(--lt-blue)] py-20 ${className}`}>
      <div data-component-id="src/app/homepage/components/StatsSection.tsx:84:6" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="84" data-component-end-line="97" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`lt-reveal mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 ${isVisible ? 'lt-visible' : ''}`}>
        <div data-component-id="src/app/homepage/components/StatsSection.tsx:85:8" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="85" data-component-end-line="96" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20gap-10%20sm%3Agrid-cols-2%20lg%3Agrid-cols-4%22%7D" className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <div data-component-id="src/app/homepage/components/StatsSection.tsx:87:12" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="87" data-component-end-line="94" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={stat.id}>
              <span data-component-id="src/app/homepage/components/StatsSection.tsx:88:14" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="88" data-component-end-line="88" data-component-file="StatsSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`lt-dot ${stat.dot} mb-4 h-2 w-2`} />
              <div data-component-id="src/app/homepage/components/StatsSection.tsx:89:14" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="89" data-component-end-line="91" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-4xl%20text-white%22%7D" className="text-4xl text-white">
                <Counter data-component-id="src/app/homepage/components/StatsSection.tsx:90:16" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="90" data-component-end-line="90" data-component-file="StatsSection.tsx" data-component-name="Counter" data-component-content="%7B%22elementName%22%3A%22Counter%22%7D" end={stat.value} suffix={stat.suffix} start={isVisible} />
              </div>
              <div data-component-id="src/app/homepage/components/StatsSection.tsx:92:14" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="92" data-component-end-line="92" data-component-file="StatsSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20text-%5B15px%5D%20font-medium%20text-white%2F90%22%7D" className="mt-2 text-[15px] font-medium text-white/90">{stat.label}</div>
              <p data-component-id="src/app/homepage/components/StatsSection.tsx:93:14" data-component-path="src/app/homepage/components/StatsSection.tsx" data-component-line="93" data-component-end-line="93" data-component-file="StatsSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-white%2F60%22%7D" className="mt-1 text-sm text-white/60">{stat.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
